import { useEffect } from 'react'

const isTypingElement = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true

  const tagName = target.tagName.toLowerCase()
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select'
}

interface UseClipboardShortcutsProps {
  onCopy?: (event: KeyboardEvent) => void
  onCut?: (event: KeyboardEvent) => void
  onPaste?: (event: KeyboardEvent) => void | Promise<void>
  onDelete?: (event: KeyboardEvent) => void
  onEscape: (event: KeyboardEvent) => void
}

function useClipboardShortcuts({
  onCopy,
  onCut,
  onPaste,
  onDelete,
  onEscape,
}: UseClipboardShortcutsProps): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape(event)
        return
      }

      if (isTypingElement(event.target)) return

      if ((event.key === 'Backspace' || event.key === 'Delete') && onDelete) {
        event.preventDefault()
        onDelete(event)
        return
      }

      const isModifierPressed = event.metaKey || event.ctrlKey
      if (!isModifierPressed) return

      const key = event.key.toLowerCase()
      const code = event.code.toLowerCase()
      const isCopyShortcut = key === 'c' || code === 'keyc'
      const isCutShortcut = key === 'x' || code === 'keyx'
      const isPasteShortcut = key === 'v' || code === 'keyv'

      if (isCopyShortcut && onCopy) {
        event.preventDefault()
        onCopy(event)
        return
      }

      if (isCutShortcut && onCut) {
        event.preventDefault()
        onCut(event)
        return
      }

      if (isPasteShortcut && onPaste) {
        event.preventDefault()
        void onPaste(event)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onCopy, onCut, onPaste, onDelete, onEscape])
}

export { useClipboardShortcuts }
